import { create } from 'zustand';
import { dollarTemplate } from '../lib/utils';
import { normalizePaperName } from '../lib/const';
import {
  MULTI_OPENING_PRODUCTS,
  isPrintSizeValidForProduct,
  findMultiOpeningProductByName,
  isOvalDecklePaper,
  isCircleDecklePaper,
} from '../lib/multiOpeningMatProducts';

const PAPER_TYPE_EXCLUDE = ['canvas', 'lustre'];

const MAT_TYPE_LABELS = {
  '8-ply': '8-Ply',
  'double-mat-4-ply': 'Double Mat 4-Ply',
};

const OPENING_SHAPE_LABELS = {
  standard: 'Standard',
  circle: 'Circle',
  oval: 'Oval',
};

const MULTI_OPENING_PRODUCT_ORDER = new Map(
  MULTI_OPENING_PRODUCTS.map((product, index) => [product.id, index]),
);

const getMultiOpeningProductOrder = (printSize) => {
  const product = findMultiOpeningProductByName(printSize?.name);
  return product ? MULTI_OPENING_PRODUCT_ORDER.get(product.id) : Number.MAX_SAFE_INTEGER;
};

const getMultiOpeningProductQuantity = (printSize) => {
  const quantity = Number.parseInt(printSize?.name, 10);
  return Number.isNaN(quantity) ? Number.MAX_SAFE_INTEGER : quantity;
};

const compareMultiOpeningProducts = (a, b) => {
  const quantityDifference =
    getMultiOpeningProductQuantity(a) - getMultiOpeningProductQuantity(b);

  return quantityDifference || getMultiOpeningProductOrder(a) - getMultiOpeningProductOrder(b);
};

const matTypeMatches = (rowMatType, matTypeText) => {
  if (!matTypeText) return true;
  if (!rowMatType) return false;
  if (rowMatType === matTypeText) return true;
  const label = (MAT_TYPE_LABELS[rowMatType] || rowMatType).toLowerCase();
  const normalized = matTypeText.toLowerCase();
  return label === normalized || normalized.includes(label) || label.includes(normalized);
};

const defaultForm = () => ({
  meta: {
    requiredInputs: ['Multi-Opening Product'],
    finishingCategoryId: undefined,
    finishingServiceId: undefined,
    finishingDetailId: undefined,
    subtotal: 0,
  },
  inputs: {
    paperType: {
      label: 'Paper Type',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      isLoading: true,
      isDisabled: false,
    },
    printSize: {
      label: 'Print Size',
      options: [],
      selectedValue: undefined,
      selectedValueText: undefined,
      selectedValuePrice: undefined,
      isLoading: false,
      isDisabled: true,
    },
    finishingType: {
      label: 'Finishing Service',
      options: [
        { value: 'matted', text: 'Matted' },
        { value: 'framed', text: 'Framed' },
      ],
      selectedValue: undefined,
      selectedValueText: undefined,
      isDisabled: true,
    },
  },
});

const useMultiOpeningMatStore = create((set, get) => ({
  editingLineItem: null,
  setEditingLineItem: (attributes) => set({ editingLineItem: attributes }),

  // Selected multi-opening product
  selectedProduct: null,
  setSelectedProduct: (product) => set({ selectedProduct: product }),

  // filter: 'standard' | 'oval' | 'circle'
  selectedOpeningType: 'standard',
  handleOpeningTypeChange: (type) => {
    set({
      selectedOpeningType: type,
      selectedProduct: null,
      openingAssignments: {},
      openingCrops: {},
      finishingCategoriesData: null,
      finishingServicesData: null,
      frameMatsData: null,
    });
    // Reset the form
    const { allPaperTypeOptions } = get();
    set((state) => ({
      form: {
        ...defaultForm(),
        inputs: {
          ...defaultForm().inputs,
          paperType: {
            ...defaultForm().inputs.paperType,
            options: state.form.inputs.paperType.options || allPaperTypeOptions,
            isLoading: false,
          },
        },
      },
    }));
  },

  // Photo assignments per opening: { [openingId]: photoObj | null }
  openingAssignments: {},
  setOpeningAssignment: (openingId, photo) =>
    set((state) => {
      const nextCrops = { ...state.openingCrops };
      delete nextCrops[openingId];
      return {
        openingAssignments: { ...state.openingAssignments, [openingId]: photo },
        openingCrops: nextCrops,
      };
    }),
  clearOpeningAssignment: (openingId) =>
    set((state) => {
      const next = { ...state.openingAssignments };
      delete next[openingId];
      const nextCrops = { ...state.openingCrops };
      delete nextCrops[openingId];
      return { openingAssignments: next, openingCrops: nextCrops };
    }),
  resetOpeningAssignments: () => set({ openingAssignments: {}, openingCrops: {} }),

  // Crop rects per opening, as % of the original photo: { [openingId]: { x, y, width, height } }
  openingCrops: {},
  setOpeningCrop: (openingId, crop, clearCroppedPhoto = false) =>
    set((state) => ({
      ...(clearCroppedPhoto && {
        openingAssignments: {
          ...state.openingAssignments,
          [openingId]: {
            ...state.openingAssignments[openingId],
            attributes: {
              ...state.openingAssignments[openingId]?.attributes,
              croppedPhotoUrl: undefined,
            },
          },
        },
      }),
      openingCrops: { ...state.openingCrops, [openingId]: crop },
    })),

  // Raw API data caches
  allPaperTypeOptions: [], // unfiltered paper type options from API
  finishingCategoriesData: null,
  finishingServicesData: null,
  frameMatsData: null,

  // Form
  form: defaultForm(),
  setForm: (updater) => {
    if (typeof updater === 'function') {
      set((state) => ({ form: updater(state.form) }));
    } else {
      set({ form: updater });
    }
    console.log('form.meta:', get().form.meta);
  },

  resetForm: () => {
    const { allPaperTypeOptions } = get();
    set({
      editingLineItem: null,
      selectedProduct: null,
      selectedOpeningType: 'standard',
      openingAssignments: {},
      openingCrops: {},
      finishingCategoriesData: null,
      finishingServicesData: null,
      frameMatsData: null,
      form: defaultForm(),
    });

    if (allPaperTypeOptions.length > 0) {
      get().applyPaperTypeOptions(allPaperTypeOptions);
    }
  },

  hydrateFromLineItem: (attributes) => {
    if (!attributes) return;

    const staticProduct = findMultiOpeningProductByName(attributes.printSizeName);
    if (!staticProduct) {
      set({ editingLineItem: attributes });
      return;
    }

    const openings = staticProduct.openings || [];
    const photographs = attributes.photographs || [];
    const openingAssignments = {};
    const openingCrops = {};

    openings.forEach((opening, index) => {
      const photo = photographs[index];
      if (!photo) return;
      const guid = photo.photographGuid || photo.id;
      if (!guid) return;

      const displayUrl = photo.photographUrl || photo.photographCroppedUrl;
      openingAssignments[opening.id] = {
        id: guid,
        type: 'lab_order_photograph',
        attributes: {
          guid,
          url: displayUrl,
          presignedPhotoUrl: displayUrl,
          croppedPhotoUrl: photo.photographCroppedUrl,
          fileName: photo.photoFileName,
        },
      };

      const cropAttrs = photo.photographAttributes;
      if (cropAttrs && photo.photographUrl) {
        openingCrops[opening.id] = {
          x: cropAttrs.cropX,
          y: cropAttrs.cropY,
          width: cropAttrs.cropW,
          height: cropAttrs.cropH,
        };
      }
    });

    set({
      editingLineItem: attributes,
      selectedOpeningType: staticProduct.openingShape === 'circle' || staticProduct.openingShape === 'oval'
        ? staticProduct.openingShape
        : 'standard',
      selectedProduct: {
        ...staticProduct,
        id: attributes.printSizeAttributes?.printSizeGuid,
        name: attributes.printSizeName,
        price: attributes.printSizeAttributes?.price,
      },
      openingAssignments,
      openingCrops,
    });

    set((state) => ({
      form: {
        ...state.form,
        meta: {
          ...state.form.meta,
          requiredInputs: ['Finishing Service'],
        },
        inputs: {
          ...state.form.inputs,
          printSize: {
            label: 'Print Size',
            options: state.form.inputs.printSize?.options || [],
            selectedValue: attributes.printSizeAttributes?.printSizeGuid,
            selectedValueText: attributes.printSizeName,
            selectedValuePrice: attributes.printSizeAttributes?.price,
            isLoading: false,
            isDisabled: false,
          },
          finishingType: {
            label: 'Finishing Service',
            options: state.form.inputs.finishingType?.options || [
              { value: 'matted', text: 'Matted' },
              { value: 'framed', text: 'Framed' },
            ],
            selectedValue: undefined,
            selectedValueText: undefined,
            isDisabled: false,
          },
        },
      },
    }));
  },

  processPaperTypeOptions: (rawOptions) => {
    const normalizedOptions = rawOptions.map((opt) => ({
      ...opt,
      text: normalizePaperName(opt.text),
    }));

    set({ allPaperTypeOptions: normalizedOptions });
    get().applyPaperTypeOptions(normalizedOptions);

    const { editingLineItem, form } = get();
    if (editingLineItem && !form.inputs.paperType?.selectedValue) {
      const savedPaperName = normalizePaperName(
        editingLineItem.printSizeAttributes?.paper || editingLineItem.paperName || '',
      );
      const matched = form.inputs.paperType.options.find((opt) => opt.text === savedPaperName);
      if (matched) {
        set((state) => ({
          form: {
            ...state.form,
            inputs: {
              ...state.form.inputs,
              paperType: {
                ...state.form.inputs.paperType,
                selectedValue: matched.value,
                selectedValueText: matched.text,
              },
            },
          },
        }));
      }
    }
  },

  applyPaperTypeOptions: (rawOptions) => {
    const filtered = rawOptions.filter((opt) => {
      const t = opt.text.toLowerCase();
      if (PAPER_TYPE_EXCLUDE.some((ex) => t.includes(ex))) return false;
      if (t.startsWith('deckled paper') && (t.includes('oval') || t.includes('circle'))) return false;
      return true;
    });

    set((state) => ({
      form: {
        ...state.form,
        inputs: {
          ...state.form.inputs,
          paperType: {
            ...state.form.inputs.paperType,
            options: filtered,
            isLoading: false,
          },
        },
      },
    }));
  },

  // When a product is selected from the first dropdown
  handleProductChange: (product) => {
    const { allPaperTypeOptions } = get();
    set({
      selectedProduct: product,
      openingAssignments: {},
      openingCrops: {},
      finishingCategoriesData: null,
      finishingServicesData: null,
      frameMatsData: null,
    });

    // Re-filter paper types for new product
    get().applyPaperTypeOptions(allPaperTypeOptions, product);

    set((state) => ({
      form: {
        meta: {
          requiredInputs: ['Paper Type', 'Print Size', 'Finishing Service'],
          finishingCategoryId: undefined,
          finishingServiceId: undefined,
          finishingDetailId: undefined,
          subtotal: 0,
        },
        inputs: {
          paperType: {
            ...state.form.inputs.paperType,
            selectedValue: undefined,
            selectedValueText: undefined,
          },
          printSize: {
            label: 'Print Size',
            options: [],
            selectedValue: undefined,
            selectedValueText: undefined,
            selectedValuePrice: undefined,
            isLoading: false,
            isDisabled: true,
          },
          finishingType: {
            label: 'Finishing Service',
            options: [
              { value: 'matted', text: 'Matted' },
              { value: 'framed', text: 'Framed' },
            ],
            selectedValue: undefined,
            selectedValueText: undefined,
            isDisabled: true,
          },
        },
      },
    }));
  },

  selectMultiOpeningApiProduct: (apiItem) => {
    if (!apiItem) {
      set({
        selectedProduct: null,
        finishingCategoriesData: null,
        finishingServicesData: null,
        frameMatsData: null,
      });
      return;
    }

    const staticProduct = findMultiOpeningProductByName(apiItem.name);
    const { openingAssignments: prevAssignments } = get();
    const newOpeningIds = new Set((staticProduct?.openings || []).map((o) => o.id));
    const carriedAssignments = Object.fromEntries(
      Object.entries(prevAssignments || {}).filter(([id]) => newOpeningIds.has(Number(id))),
    );

    set({
      selectedProduct: {
        ...staticProduct,
        id: apiItem.guid,
        name: apiItem.name,
        price: apiItem.price,
        xAspect: apiItem.xAspect,
        yAspect: apiItem.yAspect,
        masterMuseaCode: apiItem.masterMuseaCode,
      },
      openingAssignments: carriedAssignments,
      openingCrops: {},
      finishingCategoriesData: null,
      finishingServicesData: null,
      frameMatsData: null,
    });

    get().applyPaperTypeOptions(get().allPaperTypeOptions, staticProduct);

    set((state) => ({
      form: {
        meta: {
          ...state.form.meta,
          requiredInputs: ['Finishing Service'],
          finishingCategoryId: undefined,
          finishingServiceId: undefined,
          finishingDetailId: undefined,
          frameMatPricingId: undefined,
        },

        inputs: {
          paperType: state.form.inputs.paperType,
          printSize: {
            label: 'Print Size',
            options: state.form.inputs.printSize?.options || [],
            selectedValue: apiItem.guid,
            selectedValueText: apiItem.name,
            selectedValuePrice: apiItem.price,
            isLoading: false,
            isDisabled: false,
          },
          finishingType: {
            label: 'Finishing Service',
            options: state.form.inputs.finishingType?.options || [
              { value: 'matted', text: 'Matted' },
              { value: 'framed', text: 'Framed' },
            ],
            selectedValue: undefined,
            selectedValueText: undefined,
            isDisabled: false,
          },
        },
      },
    }));
  },

  setPrintSizeLoading: (isLoading) => {
    set((state) => ({
      form: {
        ...state.form,
        inputs: {
          ...state.form.inputs,
          printSize: { ...state.form.inputs.printSize, isLoading },
        },
      },
    }));
  },

  processPrintSizeData: (response) => {
    const { selectedProduct } = get();

    // React Query and this store receive the same response array. Keep the
    // product dropdown stable across paper types by sorting on the leading
    // opening quantity. Products with the same quantity retain their canonical
    // layout order. Deckle paper filtering still happens independently.
    response.sort(compareMultiOpeningProducts);

    const allSizes = [...response].sort((a, b) => {
      if (Number(a.xAspect) !== Number(b.xAspect)) return Number(a.xAspect) - Number(b.xAspect);
      return Number(a.yAspect) - Number(b.yAspect);
    });

    const sizeOptions = allSizes
      .map((ps) => ({
        value: ps.guid,
        text: `${ps.name} (${dollarTemplate(ps.price)})`,
        price: ps.price,
        xAspectRatio: ps.xAspect,
        yAspectRatio: ps.yAspect,
      }))
      .filter((opt) => isPrintSizeValidForProduct(opt.text, selectedProduct));

    set((state) => ({
      form: {
        ...state.form,
        inputs: {
          ...state.form.inputs,
          printSize: {
            ...state.form.inputs.printSize,
            options: sizeOptions,
            isLoading: false,
            isDisabled: false,
          },
        },
      },
    }));
  },

  setFinishingServiceLoading: (isLoading) => {
    set((state) => ({
      form: {
        ...state.form,
        inputs: {
          ...state.form.inputs,
          finishingType: { ...state.form.inputs.finishingType, isLoading },
        },
      },
    }));
  },

  processFinishingCategoriesData: (response) => {
    set({ finishingCategoriesData: response });
    set((state) => ({
      form: {
        ...state.form,
        inputs: {
          ...state.form.inputs,
          finishingType: {
            ...state.form.inputs.finishingType,
            isDisabled: false,
            isLoading: false,
          },
        },
      },
    }));

    const { editingLineItem, form } = get();
    if (editingLineItem && !form.inputs.finishingType?.selectedValue) {
      const savedCategory = (
        editingLineItem.finishingCategoryName || editingLineItem.finishingService?.finishingCategory || ''
      ).toLowerCase();
      if (savedCategory.includes('frame') || savedCategory.includes('fram')) {
        get().handleFinishingTypeChange('framed', 'Framed');
      } else if (savedCategory.includes('mat')) {
        get().handleFinishingTypeChange('matted', 'Matted');
        const matTypeOptions = get().form.inputs.matType?.options || [];
        const matched = matTypeOptions.find(
          (opt) => savedCategory.includes(opt.text.toLowerCase()) || savedCategory === opt.text.toLowerCase(),
        );
        if (matched) {
          get().handleMatTypeChange(matched.value, matched.text);
          // Opening shape is restored once the real frame-mat rows for this
          // mat type arrive from the API — see processFrameMatOptions.
        }
      }
    }
  },

  // Called when user picks Matted or Framed
  handleFinishingTypeChange: (value, text) => {
    const { finishingCategoriesData } = get();

    if (!finishingCategoriesData) return;

    if (value === 'matted') {
      const matTypeCategoryOptions = finishingCategoriesData.data
        .filter((cat) => cat.attributes.finishingCategoryPrimaryName?.toLowerCase() === 'matting')
        .map((cat) => ({
          value: cat.attributes.guid,
          text: cat.attributes.finishingCategoryDetailName || cat.attributes.name,
        }));

      set((state) => ({
        form: {
          meta: {
            ...state.form.meta,
            finishingCategoryId: undefined,
            finishingServiceId: undefined,
            finishingDetailId: undefined,
            requiredInputs: ['Mat Type', 'Mat Opening Shape'],
          },
          inputs: {
            paperType: state.form.inputs.paperType,
            printSize: state.form.inputs.printSize,
            finishingType: {
              ...state.form.inputs.finishingType,
              selectedValue: value,
              selectedValueText: text,
            },
            matType: {
              label: 'Mat Type',
              options: matTypeCategoryOptions,
              selectedValue: undefined,
              selectedValueText: undefined,
              isDisabled: false,
            },
            // Hidden until a Mat Type is picked — see handleMatTypeChange /
            // processFrameMatOptions, which reveal it once the real shape
            // options for that mat type are known (and re-hide it if
            // "Standard" turns out to be the only one offered).
            matOpeningShape: {
              label: 'Mat Opening Shape',
              options: get().getOpeningShapeOptions(),
              selectedValue: undefined,
              selectedValueText: undefined,
              isDisabled: false,
              isHidden: true,
            },
          },
        },
      }));
      return;
    }

    if (value === 'framed') {
      // Find Framing sub-categories (Contemporary Frame, Hardwood Frame, etc.)
      const framingSubcategories = finishingCategoriesData.data
        .filter((cat) => {
          const name = cat.attributes.finishingCategoryDetailName?.toLowerCase() || '';
          return name.includes('contemporary') || name.includes('hardwood');
        })
        .map((cat) => ({
          value: cat.attributes.guid,
          text: cat.attributes.finishingCategoryDetailName,
        }));

      set((state) => ({
        form: {
          meta: {
            ...state.form.meta,
            finishingCategoryId: undefined,
            finishingServiceId: undefined,
            finishingDetailId: undefined,
            requiredInputs: ['Frame Name', 'Mat Type', 'Acrylic'],
          },
          inputs: {
            paperType: state.form.inputs.paperType,
            printSize: state.form.inputs.printSize,
            finishingType: {
              ...state.form.inputs.finishingType,
              selectedValue: value,
              selectedValueText: text,
            },
            frameType: {
              label: 'Frame Name',
              options: framingSubcategories,
              selectedValue: undefined,
              selectedValueText: undefined,
              isDisabled: false,
              isLoading: false,
            },
          },
        },
      }));

      {
        const { editingLineItem } = get();
        if (editingLineItem) {
          const savedCategory = (
            editingLineItem.finishingCategoryName || editingLineItem.finishingService?.finishingCategory || ''
          ).toLowerCase();
          const matched = framingSubcategories.find((opt) => {
            const t = opt.text?.toLowerCase() || '';
            return t === savedCategory || savedCategory.includes(t) || t.includes(savedCategory);
          });
          if (matched) {
            set((state) => ({
              form: {
                ...state.form,
                meta: { ...state.form.meta, finishingCategoryId: matched.value },
                inputs: {
                  ...state.form.inputs,
                  frameType: {
                    ...state.form.inputs.frameType,
                    selectedValue: matched.value,
                    selectedValueText: matched.text,
                  },
                },
              },
            }));
          }
        }
      }
    }
  },

  // Returns the allowed opening shape options for the current (or given)
  // mat type, derived from the real frame_mats API rows (frameMatsData)
  // instead of a hardcoded per-product shape. Falls back to Standard-only
  // while that data hasn't loaded yet.
  getOpeningShapeOptions: (matTypeText) => {
    const rows = get().frameMatsData?.data || [];
    const shapes = Array.from(
      new Set(
        rows
          .filter((row) => matTypeMatches(row.attributes?.matType, matTypeText))
          .map((row) => row.attributes?.openingShape)
          .filter(Boolean),
      ),
    );
    // if (shapes.length === 0) return [{ value: 'standard', text: 'Standard' }];
    return shapes.map((value) => ({ value, text: OPENING_SHAPE_LABELS[value] || value }));
  },

  // Returns the opening-shape option that should be auto-selected instead of
  // asking the user, or undefined when there's a real choice to make.
  // Priority:
  //  1. An Oval/Circle Deckle paper type forces the matching shape — the
  //     print itself is already that shape, so the mat opening has to
  //     match, regardless of what the frame_mats API happens to return for
  //     this specific mat type (its rows may simply not include every
  //     shape for every mat type).
  //  2. Otherwise, when getOpeningShapeOptions() resolved to exactly one
  //     choice (Standard, Circle, or Oval), there's nothing else to pick.
  getAutoOpeningShapeOption: (matTypeText) => {
    const paperTypeText = get().form.inputs.paperType?.selectedValueText || '';
    if (isOvalDecklePaper(paperTypeText)) {
      return { value: 'oval', text: OPENING_SHAPE_LABELS.oval };
    }
    if (isCircleDecklePaper(paperTypeText)) {
      return { value: 'circle', text: OPENING_SHAPE_LABELS.circle };
    }

    const options = get().getOpeningShapeOptions(matTypeText);
    return options.length === 1 ? options[0] : undefined;
  },

  handleMatTypeChange: (value, text) => {
    set((state) => ({
      form: {
        ...state.form,
        meta: {
          ...state.form.meta,
          matTypeValue: value,
          finishingCategoryId: value,
          finishingServiceId: undefined,
          frameMatPricingId: undefined,
        },
        inputs: {
          ...state.form.inputs,
          matType: {
            ...state.form.inputs.matType,
            selectedValue: value,
            selectedValueText: text,
          },
          // Real opening shape options depend on this mat type and are only
          // known once the frame_mats API responds for it — see
          // processFrameMatOptions, which fills these back in.
          ...(state.form.inputs.matOpeningShape && {
            matOpeningShape: {
              ...state.form.inputs.matOpeningShape,
              options: [],
              selectedValue: undefined,
              selectedValueText: undefined,
              isLoading: true,
              isDisabled: true,
              isHidden: true,
            },
          }),
        },
      },
    }));
  },

  handleMatOpeningShapeChange: (value, text) => {
    const matTypeText = get().form.inputs.matType?.selectedValueText;
    const frameMatPricingId = get().resolveFrameMatPricingId(matTypeText, value);

    set((state) => {
      const matTypeSelected = state.form.inputs.matType?.selectedValue;
      return {
        form: {
          ...state.form,
          meta: {
            ...state.form.meta,
            requiredInputs: matTypeSelected ? [] : ['Mat Type'],
            frameMatPricingId,
          },
          inputs: {
            ...state.form.inputs,
            matOpeningShape: {
              ...state.form.inputs.matOpeningShape,
              selectedValue: value,
              selectedValueText: text,
            },
          },
        },
      };
    });
  },

  // Called when finishingServices API returns results for a frame type
  processFinishingServicesData: (response) => {
    const { form } = get();
    const flow = form.inputs.finishingType?.selectedValue;
    if (flow !== 'framed' && flow !== 'matted') return;

    set({ finishingServicesData: response });

    if (flow === 'matted') {
      const row = response.data?.[0];
      set((state) => ({
        form: {
          ...state.form,
          meta: {
            ...state.form.meta,
            finishingServiceId: row?.id,
          },
          inputs: {
            ...state.form.inputs,
            matType: {
              ...state.form.inputs.matType,
              selectedValuePrice: row?.attributes?.price,
            },
          },
        },
      }));
      return;
    }

    const frameTypeText = form.inputs.frameType?.selectedValueText;

    const isHardwood = frameTypeText?.toLowerCase().includes('hard');
    const isContemporary = frameTypeText?.toLowerCase().includes('contemp');
    const isDesktop = frameTypeText?.toLowerCase().includes('desktop');
    const isFloatCanvas = frameTypeText?.toLowerCase().includes('float');

    let frames = [];
    if (isContemporary || isDesktop) {
      frames = response.data.map((s) => ({
        value: s.id,
        name: s.attributes.frameWoodPricing,
        price: parseFloat(s.attributes.price),
      }));
    } else if (isHardwood && !isFloatCanvas) {
      frames = response.data.map((s) => ({
        value: s.id,
        name: s.attributes.frameWoodAndProfileName,
        price: parseFloat(s.attributes.price),
      }));
    } else if (isFloatCanvas) {
      frames = response.data.map((s) => {
        const desc = s.attributes.description || '';
        const shortName = desc.split(' for a')[0].replace(': ', ' - ');
        return { value: s.id, name: shortName || desc, price: parseFloat(s.attributes.price) };
      });
    }

    const unique = Array.from(new Map(frames.map((f) => [f.name, f])).values())
      .sort((a, b) => a.name.localeCompare(b.name));

    const frameOptions = unique.map((f) => ({ value: f.value, text: f.name, price: f.price }));

    set((state) => ({
      form: {
        ...state.form,
        meta: {
          ...state.form.meta,
          requiredInputs: isHardwood
            ? ['Frame Wood', 'Frame Stain', 'Mat Type', 'Acrylic']
            : ['Frame Wood', 'Mat Type', 'Acrylic'],
        },
        inputs: {
          ...state.form.inputs,
          frameType: state.form.inputs.frameType,
          frameName: {
            label: 'Frame Wood',
            options: frameOptions,
            selectedValue: undefined,
            selectedValueText: undefined,
            selectedValuePrice: undefined,
            isLoading: false,
            isDisabled: false,
          },
          // Explicitly clear (not just omit) so a stale Frame Stain from a
          // previous Hardwood selection doesn't linger when switching to a
          // frame type (e.g. Contemporary) that doesn't use it.
          frameStain: isHardwood
            ? {
              label: 'Frame Stain',
              options: [],
              selectedValue: undefined,
              selectedValueText: undefined,
              isLoading: false,
              isDisabled: true,
            }
            : undefined,
          matTypeFramed: {
            label: 'Mat Type',
            options: [],
            selectedValue: undefined,
            selectedValueText: undefined,
            isLoading: true,
            isDisabled: true,
          },
          matOpeningShapeFramed: {
            label: 'Mat Opening Shape',
            options: get().getOpeningShapeOptions(),
            selectedValue: undefined,
            selectedValueText: undefined,
            isDisabled: true,
          },
          acrylic: {
            label: 'Acrylic',
            options: [],
            selectedValue: undefined,
            selectedValueText: undefined,
            selectedValuePrice: undefined,
            isLoading: false,
            isDisabled: true,
          },
        },
      },
    }));

    const { editingLineItem } = get();
    if (editingLineItem) {
      const serviceGuid = editingLineItem.finishingService?.finishingServiceGuid;
      const serviceName = editingLineItem.finishingService?.finishingService;
      const matchedService = serviceGuid
        ? response.data.find((s) => s.id === serviceGuid || s.attributes.guid === serviceGuid)
        : null;

      let matchedFrameName;
      if (matchedService) {
        if (isContemporary || isDesktop) matchedFrameName = matchedService.attributes.frameWoodPricing;
        else if (isHardwood && !isFloatCanvas) matchedFrameName = matchedService.attributes.frameWoodAndProfileName;
        else if (isFloatCanvas) {
          const desc = matchedService.attributes.description || '';
          matchedFrameName = desc.split(' for a')[0].replace(': ', ' - ') || desc;
        }
      }

      const matchedFrameOption =
        frameOptions.find((f) => f.text === matchedFrameName) ||
        frameOptions.find((f) => f.text === serviceName);

      if (matchedFrameOption) {
        get().handleFrameNameChange(matchedFrameOption.value, matchedFrameOption.text, matchedFrameOption.price);
      }
    }
  },

  processFrameMatOptions: (response) => {
    set({ frameMatsData: response });

    const { editingLineItem, finishingServicesData, form } = get();

    if (form.inputs.finishingType?.selectedValue === 'matted' && form.inputs.matOpeningShape) {
      const matTypeText = form.inputs.matType?.selectedValueText;
      const shapeOptions = get().getOpeningShapeOptions(matTypeText);
      const autoShape = get().getAutoOpeningShapeOption(matTypeText);

      set((state) => ({
        form: {
          ...state.form,
          inputs: {
            ...state.form.inputs,
            matOpeningShape: {
              ...state.form.inputs.matOpeningShape,
              options: shapeOptions,
              isLoading: false,
              isDisabled: false,
              isHidden: !!autoShape,
            },
          },
        },
      }));

      if (autoShape) {
        get().handleMatOpeningShapeChange(autoShape.value, autoShape.text);
        return;
      }

      const savedFrameMat = editingLineItem?.frameMat;
      const currentShapeValue = get().form.inputs.matOpeningShape?.selectedValue;

      if (!currentShapeValue && savedFrameMat?.openingShape) {
        // Editing an existing line item: restore its saved opening shape now
        // that the real frame-mat rows for this mat type are available.
        const savedShapeOption = shapeOptions.find(
          (opt) => opt.value === savedFrameMat.openingShape,
        );
        if (savedShapeOption) {
          get().handleMatOpeningShapeChange(savedShapeOption.value, savedShapeOption.text);
        }
      } else if (
        currentShapeValue &&
        !shapeOptions.some((opt) => opt.value === currentShapeValue)
      ) {
        // The previously selected shape isn't offered for this mat type
        // (e.g. mat type just changed) — clear it so the user re-picks.
        set((state) => ({
          form: {
            ...state.form,
            meta: { ...state.form.meta, frameMatPricingId: undefined, requiredInputs: ['Mat Opening Shape'] },
            inputs: {
              ...state.form.inputs,
              matOpeningShape: {
                ...state.form.inputs.matOpeningShape,
                selectedValue: undefined,
                selectedValueText: undefined,
              },
            },
          },
        }));
      } else if (currentShapeValue) {
        // Shape is still valid but the frame-mat rows just (re)loaded —
        // re-resolve the pricing id against the fresh rows.
        const frameMatPricingId = get().resolveFrameMatPricingId(matTypeText, currentShapeValue);
        set((state) => ({
          form: { ...state.form, meta: { ...state.form.meta, frameMatPricingId } },
        }));
      }
    }

    if (!editingLineItem) return;
    if (form.inputs.finishingType?.selectedValue !== 'framed') return;
    if (!form.inputs.matTypeFramed || form.inputs.matTypeFramed.selectedValue) return;

    const rows = response?.data || [];
    const apiMatTypeOptions = Array.from(
      new Set(
        rows.map((row) => row.attributes?.matType).filter((value) => MAT_TYPE_LABELS[value]),
      ),
    ).map((value) => ({ value, text: MAT_TYPE_LABELS[value] }));
    const options = apiMatTypeOptions;

    const savedFrameMat = editingLineItem.frameMat;
    if (savedFrameMat?.matType) {
      const matchedOption = options.find((opt) => opt.value === savedFrameMat.matType);
      if (matchedOption) {
        get().handleMatTypeFramedChange(matchedOption.value, matchedOption.text, {
          openingShapeValue: savedFrameMat.openingShape,
          frameMatPricingId: savedFrameMat.guid,
        });
        return;
      }
    }

    const serviceGuid = editingLineItem.finishingService?.finishingServiceGuid;
    const matchedService = serviceGuid && finishingServicesData
      ? finishingServicesData.data.find((s) => s.id === serviceGuid || s.attributes.guid === serviceGuid)
      : null;
    const matName = (matchedService?.attributes?.frameMatName || '').toLowerCase().trim();
    const isNoMat = !matName || matName.includes('no mat') || matName.includes('no-mat');

    const matchedOption = isNoMat
      ? options.find((opt) => opt.value === 'no-mat')
      : options.find(
        (opt) => matName.includes(opt.value.toLowerCase()) || matName.includes(opt.text.toLowerCase()),
      ) ||
        (matName.includes('8') && options.find((opt) => opt.value.includes('8'))) ||
        ((matName.includes('4') || matName.includes('double')) &&
          options.find((opt) => opt.value.includes('4')));

    if (matchedOption) {
      get().handleMatTypeFramedChange(matchedOption.value, matchedOption.text);
    }
  },

  handleFrameNameChange: (value, text, price) => {
    const { finishingServicesData, form } = get();
    if (!finishingServicesData) return;

    const frameTypeText = form.inputs.frameType?.selectedValueText;
    const isHardwood = frameTypeText?.toLowerCase().includes('hard');
    const isFloatCanvas = frameTypeText?.toLowerCase().includes('float');
    const isContemporary = frameTypeText?.toLowerCase().includes('contemp');
    const isDesktop = frameTypeText?.toLowerCase().includes('desktop');

    // Collect frame mat names for this frame selection
    let frameMats = [];
    if (isContemporary || isDesktop) {
      frameMats = finishingServicesData.data
        .filter((s) => s.attributes.frameWoodPricing === text)
        .map((s) => s.attributes.frameMatName || '');
    } else if (isHardwood && !isFloatCanvas) {
      frameMats = finishingServicesData.data
        .filter((s) => s.attributes.frameWoodAndProfileName === text)
        .map((s) => s.attributes.frameMatName || '');
    } else if (isFloatCanvas) {
      frameMats = finishingServicesData.data
        .filter((s) => {
          const desc = s.attributes.description || '';
          const shortName = desc.split(' for a')[0].replace(': ', ' - ');
          return shortName === text || desc === text;
        })
        .map((s) => s.attributes.frameMatName || '');
    }

    const uniqueMats = [...new Set(frameMats)];

    set((state) => ({
      form: {
        ...state.form,
        inputs: {
          ...state.form.inputs,
          frameName: {
            ...state.form.inputs.frameName,
            selectedValue: value,
            selectedValueText: text,
            selectedValuePrice: price,
          },
          // Same as above: clear Frame Stain outright for non-Hardwood frames
          // instead of just leaving a stale value/object in place.
          frameStain: isHardwood
            ? {
              ...state.form.inputs.frameStain,
              isDisabled: false,
            }
            : undefined,
          matTypeFramed: {
            ...state.form.inputs.matTypeFramed,
            isDisabled: false,
          },
          // Store the available mat names for acrylic lookup later
          _frameMats: uniqueMats,
        },
      },
    }));
  },

  handleFrameStainChange: (value, text) => {
    set((state) => ({
      form: {
        ...state.form,
        meta: { ...state.form.meta, finishingDetailId: value },
        inputs: {
          ...state.form.inputs,
          frameStain: {
            ...state.form.inputs.frameStain,
            selectedValue: value,
            selectedValueText: text,
          },
        },
      },
    }));
  },

  processFrameStainOptions: (response) => {
    if (!get().form.inputs.frameStain) return;

    const stainOptions = response.data.map((d) => ({
      value: d.attributes.guid,
      text: d.attributes.name,
    }));

    set((state) => ({
      form: {
        ...state.form,
        inputs: {
          ...state.form.inputs,
          frameStain: {
            ...state.form.inputs.frameStain,
            options: stainOptions,
            isDisabled: false,
          },
        },
      },
    }));

    const { editingLineItem } = get();
    if (editingLineItem) {
      const detailGuid = editingLineItem.finishingService?.finishingServiceDetailGuid;
      const detailName = editingLineItem.finishingService?.finishingServiceDetail;
      const matched =
        stainOptions.find((s) => s.value === detailGuid) || stainOptions.find((s) => s.text === detailName);
      if (matched) {
        get().handleFrameStainChange(matched.value, matched.text);
      }
    }
  },

  resolveFrameMatPricingId: (matTypeValue, openingShapeValue) => {
    if (!matTypeValue || matTypeValue === 'no-mat') return undefined;
    console.log('Frame Mats Type', matTypeValue, 'opening shape', openingShapeValue)
    const rows = get().frameMatsData?.data || [];
    const shape = openingShapeValue && openingShapeValue !== 'none' ? openingShapeValue : 'standard';
    const normalized = matTypeValue.toLowerCase();
    const match = rows.find((row) => {
      const slug = row.attributes?.matType;
      if (!slug || row.attributes?.openingShape !== shape) return false;
      if (slug === matTypeValue) return true;
      const label = (MAT_TYPE_LABELS[slug] || slug).toLowerCase();
      return label === normalized || normalized.includes(label) || label.includes(normalized);
    });
    return match?.id;
  },

  handleMatTypeFramedChange: (value, text, overrides = {}) => {
    const { openingShapeValue: presetShapeValue, frameMatPricingId: presetFrameMatPricingId } = overrides;
    const openingShapeOpts = get().getOpeningShapeOptions(text);
    const skipShape = value === 'no-mat';
    const presetShapeOption = presetShapeValue
      ? openingShapeOpts.find((opt) => opt.value === presetShapeValue)
      : undefined;
    const resolvedFrameMatPricingId = presetShapeOption ? presetFrameMatPricingId : undefined;
    // Only one shape should be offered for this mat type — either forced by
    // an Oval/Circle Deckle paper selection or because the data only offers
    // one — so auto-select it instead of showing a one-option dropdown.
    const autoShape = !skipShape ? get().getAutoOpeningShapeOption(text) : undefined;

    set((state) => {
      const newInputs = {
        ...state.form.inputs,
        matTypeFramed: {
          ...state.form.inputs.matTypeFramed,
          selectedValue: value,
          selectedValueText: text,
        },
        matOpeningShapeFramed: {
          ...state.form.inputs.matOpeningShapeFramed,
          options: openingShapeOpts,
          isDisabled: skipShape,
          isHidden: !!autoShape,
          selectedValue: skipShape ? 'none' : presetShapeOption?.value,
          selectedValueText: skipShape ? 'None' : presetShapeOption?.text,
        },
        acrylic: {
          ...state.form.inputs.acrylic,
          isDisabled: false,
        },
      };

      return {
        form: {
          ...state.form,
          meta: {
            ...state.form.meta,
            requiredInputs: skipShape || presetShapeOption || autoShape ? ['Acrylic'] : ['Mat Opening Shape', 'Acrylic'],
            frameMatPricingId: skipShape ? undefined : resolvedFrameMatPricingId,
          },
          inputs: newInputs,
        },
      };
    });

    // Compute acrylic options for this frame + mat combination
    get().computeAcrylicOptions(value);
    const { editingLineItem } = get();
    if (autoShape) {
      get().handleMatOpeningShapeFramedChange(autoShape.value, autoShape.text);
    } else if (editingLineItem && !skipShape && !presetShapeOption) {
      get().handleMatOpeningShapeFramedChange('standard', 'Standard');
    }
  },

  computeAcrylicOptions: (matTypeValue) => {
    const { finishingServicesData, form } = get();
    if (!finishingServicesData) return;

    const frameTypeText = form.inputs.frameType?.selectedValueText;
    const frameText = form.inputs.frameName?.selectedValueText;
    const isContemporary = frameTypeText?.toLowerCase().includes('contemp');
    const isDesktop = frameTypeText?.toLowerCase().includes('desktop');
    const isHardwood = frameTypeText?.toLowerCase().includes('hard');
    const isFloatCanvas = frameTypeText?.toLowerCase().includes('float');

    const matchesMat = (svc) => {
      const matName = (svc.attributes.frameMatName || '').toLowerCase();
      return matName !== '' || matTypeValue === 'no-mat';
    };

    let acrylics = [];
    if (isContemporary || isDesktop) {
      acrylics = finishingServicesData.data
        .filter((s) => s.attributes.frameWoodPricing === frameText && matchesMat(s))
        .map((s) => ({ value: s.attributes.guid, text: s.attributes.frameGlassName || '', price: s.attributes.price }));
    } else if (isHardwood && !isFloatCanvas) {
      acrylics = finishingServicesData.data
        .filter((s) => s.attributes.frameWoodAndProfileName === frameText && matchesMat(s))
        .map((s) => ({ value: s.attributes.guid, text: s.attributes.frameGlassName || '', price: s.attributes.price }));
    } else if (isFloatCanvas) {
      acrylics = finishingServicesData.data
        .filter((s) => {
          const desc = s.attributes.description || '';
          const shortName = desc.split(' for a')[0].replace(': ', ' - ');
          return (shortName === frameText || desc === frameText) && matchesMat(s);
        })
        .map((s) => ({ value: s.attributes.guid, text: s.attributes.frameGlassName || '', price: s.attributes.price }));
    }

    // Deduplicate by text
    const filtered = acrylics.reduce((acc, a) => {
      if (!a.text || !a.value) return acc;
      const key = a.text.trim().toLowerCase();
      if (!acc.some((x) => x.text.trim().toLowerCase() === key)) acc.push(a);
      return acc;
    }, []);

    set((state) => ({
      form: {
        ...state.form,
        inputs: {
          ...state.form.inputs,
          acrylic: {
            ...state.form.inputs.acrylic,
            options: filtered,
            isLoading: false,
            isDisabled: false,
          },
        },
      },
    }));

    const { editingLineItem } = get();
    if (editingLineItem) {
      const detailGuid = editingLineItem.finishingService?.finishingServiceDetailThreeGuid;
      const detailText = editingLineItem.finishingService?.formattedDetailThree;
      const matched =
        filtered.find((a) => a.value === detailGuid) || filtered.find((a) => a.text === detailText);
      if (matched) {
        get().handleAcrylicChange(matched.value, matched.text, matched.price);
      }
    }
  },

  handleMatOpeningShapeFramedChange: (value, text) => {
    const matTypeValue = get().form.inputs.matTypeFramed?.selectedValue;
    const frameMatPricingId = get().resolveFrameMatPricingId(matTypeValue, value);

    set((state) => {
      const acrylicSelected = state.form.inputs.acrylic?.selectedValue;
      return {
        form: {
          ...state.form,
          meta: {
            ...state.form.meta,
            requiredInputs: acrylicSelected ? [] : ['Acrylic'],
            frameMatPricingId,
          },
          inputs: {
            ...state.form.inputs,
            matOpeningShapeFramed: {
              ...state.form.inputs.matOpeningShapeFramed,
              selectedValue: value,
              selectedValueText: text,
            },
          },
        },
      };
    });
  },

  handleAcrylicChange: (value, text, price) => {
    set((state) => ({
      form: {
        ...state.form,
        meta: {
          ...state.form.meta,
          finishingServiceId: value,
          requiredInputs: [],
        },
        inputs: {
          ...state.form.inputs,
          acrylic: {
            ...state.form.inputs.acrylic,
            selectedValue: value,
            selectedValueText: text,
            selectedValuePrice: price,
          },
        },
      },
    }));
  },

  // Validation: all openings assigned + required inputs satisfied
  isFormValid: () => {
    const { form, selectedProduct, openingAssignments } = get();
    if (!selectedProduct?.openings) return false;
    if (form.meta.requiredInputs?.length > 0) return false;
    const allAssigned = selectedProduct.openings.every(
      (o) => openingAssignments[o.id],
    );
    return allAssigned;
  },

  getMissingOpenings: () => {
    const { selectedProduct, openingAssignments } = get();
    if (!selectedProduct?.openings) return 0;
    return selectedProduct.openings.filter((o) => !openingAssignments[o.id]).length;
  },

  // Compute subtotal from the selected prices
  calculateSubtotal: () => {
    const { form } = get();
    const acrylicPrice = parseFloat(form.inputs.acrylic?.selectedValuePrice);
    if (!Number.isNaN(acrylicPrice)) return acrylicPrice.toFixed(2);
    const matTypePrice = parseFloat(form.inputs.matType?.selectedValuePrice);
    if (!Number.isNaN(matTypePrice)) return matTypePrice.toFixed(2);
    const printPrice = parseFloat(form.inputs.printSize?.selectedValuePrice);
    if (!Number.isNaN(printPrice)) return printPrice.toFixed(2);
    return '0.00';
  },

  // Build the add-to-cart request body
  buildAddToCartBody: (quantity, notesValue) => {
    const { form, selectedProduct, openingAssignments, openingCrops } = get();
    const openings = selectedProduct?.openings || [];

    const photographs = openings
      .map((o) => {
        const photo = openingAssignments[o.id];
        const guid = photo?.attributes?.guid;
        if (!guid) return null;

        const crop = openingCrops[o.id] || {};
        const aspect =
          o.printWidth && o.printHeight ? o.printWidth / o.printHeight : undefined;

        return {
          source_photograph_guid: guid,
          crop_x: crop.x,
          crop_y: crop.y,
          crop_w: crop.width,
          crop_h: crop.height,
          photo_meta: {
            x: crop.x,
            y: crop.y,
            width: crop.width,
            height: crop.height,
            aspect,
            imageGuid: guid,
          },
        };
      })
      .filter(Boolean);

    console.log('Frame Mat Id', form.meta.frameMatPricingId)
    return {
      multi_opening_line_item: {
        print_size_pricing_id: form.inputs.printSize?.selectedValue,
        paper_pricing_id: form.inputs.paperType?.selectedValue,
        finishing_category_pricing_id: form.meta.finishingCategoryId,
        finishing_service_pricing_id: form.meta.finishingServiceId,
        finishing_service_detail_pricing_id: form.meta.finishingDetailId,
        frame_mat_pricing_id: form.meta.frameMatPricingId,
        qty: parseInt(quantity, 10),
        notes: notesValue || '',
        internal_notes: form.meta.internalNotes || '',
        external_id: form.meta.externalId || '',
        photographs,
      },
    };
  },
}));

export default useMultiOpeningMatStore;
